import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEOMeta from '../components/SEOMeta';

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Header = styled.header`
  background: white;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const Content = styled.div`
  padding: 24px 16px;
  background: white;
  min-height: calc(100dvh - 60px);
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FAQItem = styled.details`
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 10px;
  overflow: hidden;
  
  &[open] summary {
    border-bottom: 1px solid #e2e8f0;
  }
`;

const FAQQuestion = styled.summary`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  padding: 14px 16px;
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &::-webkit-details-marker {
    display: none;
  }
  
  &::after {
    content: '+';
    font-size: 18px;
    font-weight: 400;
    color: #64748b;
  }
  
  details[open] &::after {
    content: '−';
  }
`;

const FAQAnswer = styled.div`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  padding: 14px 16px;
`;

const List = styled.ul`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 8px 0 0;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 4px;
`;

const FAQPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <SEOMeta 
        title="FAQ - Pertanyaan Umum | KinderGame.id"
        description="Pertanyaan yang sering diajukan tentang Kindergame"
      />
      
      <Header>
        <BackButton onClick={() => navigate(-1)}>←</BackButton>
        <HeaderTitle>FAQ</HeaderTitle>
      </Header>

      <Content>
        <Section>
          <SectionTitle>📋 Umum</SectionTitle>
          
          <FAQItem>
            <FAQQuestion>Apa itu Kindergame?</FAQQuestion>
            <FAQAnswer>
              Kindergame adalah platform game edukasi interaktif untuk anak usia 3-8 tahun. 
              Kami menyediakan berbagai permainan yang dirancang untuk mengembangkan kemampuan 
              kognitif, motorik, dan kreativitas anak sambil bermain.
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Apakah Kindergame gratis?</FAQQuestion>
            <FAQAnswer>
              Kindergame menyediakan beberapa game gratis yang bisa langsung dimainkan. 
              Untuk akses ke seluruh koleksi game premium, tersedia paket berlangganan 
              bulanan dan tahunan.
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Untuk usia berapa Kindergame cocok?</FAQQuestion>
            <FAQAnswer>
              Kindergame dirancang untuk anak usia 3-8 tahun. Setiap game memiliki 
              tingkat kesulitan yang disesuaikan dengan perkembangan anak.
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Apakah konten Kindergame aman untuk anak?</FAQQuestion>
            <FAQAnswer>
              Ya, 100% aman. Semua konten dikurasi khusus untuk anak-anak. Tidak ada 
              iklan pihak ketiga, tidak ada chat dengan orang asing, dan tidak ada 
              konten yang tidak pantas.
            </FAQAnswer>
          </FAQItem>
        </Section>

        <Section>
          <SectionTitle>👤 Akun & Berlangganan</SectionTitle>

          <FAQItem>
            <FAQQuestion>Bagaimana cara mendaftar?</FAQQuestion>
            <FAQAnswer>
              Klik tombol "Daftar" di halaman utama, masukkan email dan buat password. 
              Verifikasi email, dan akun Anda siap digunakan.
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Apa saja pilihan paket berlangganan?</FAQQuestion>
            <FAQAnswer>
              <List>
                <ListItem><strong>1 Bulan:</strong> Rp 20.000</ListItem>
                <ListItem><strong>Setahun:</strong> Rp 50.000 (🔥 Paling Laris - hemat 50%)</ListItem>
                <ListItem><strong>4 Tahun:</strong> Rp 200.000 (💎 Paling Hemat - hemat 50%)</ListItem>
              </List>
              <br />
              <em>*Harga dapat berubah sewaktu-waktu</em>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Bagaimana cara berlangganan?</FAQQuestion>
            <FAQAnswer>
              Setelah login, pilih menu "Berlangganan", pilih paket yang diinginkan, 
              dan selesaikan pembayaran melalui metode yang tersedia.
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Bagaimana cara membatalkan langganan?</FAQQuestion>
            <FAQAnswer>
              Buka menu "Akun" → "Langganan" → "Batalkan Langganan". Anda tetap bisa 
              mengakses fitur premium hingga periode langganan berakhir.
            </FAQAnswer>
          </FAQItem>
        </Section>

        <Section>
          <SectionTitle>🔧 Teknis</SectionTitle>

          <FAQItem>
            <FAQQuestion>Game tidak bisa loading, apa yang harus dilakukan?</FAQQuestion>
            <FAQAnswer>
              <List>
                <ListItem>Pastikan koneksi internet stabil</ListItem>
                <ListItem>Refresh halaman atau tutup dan buka kembali aplikasi</ListItem>
                <ListItem>Hapus cache browser (jika menggunakan web)</ListItem>
                <ListItem>Jika masih bermasalah, hubungi tim support kami</ListItem>
              </List>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Perangkat apa saja yang didukung?</FAQQuestion>
            <FAQAnswer>
              Kindergame dapat diakses melalui:
              <List>
                <ListItem>Browser web (Chrome, Safari, Firefox, Edge)</ListItem>
                <ListItem>Tablet dan smartphone (Android & iOS)</ListItem>
                <ListItem>Komputer/laptop (Windows & Mac)</ListItem>
              </List>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Apakah bisa dimainkan offline?</FAQQuestion>
            <FAQAnswer>
              Saat ini Kindergame memerlukan koneksi internet untuk dimainkan.
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Suara/musik tidak keluar, bagaimana?</FAQQuestion>
            <FAQAnswer>
              Pastikan volume perangkat tidak dalam mode silent. Cek juga pengaturan 
              suara di dalam game (ikon speaker di pojok layar).
            </FAQAnswer>
          </FAQItem>
        </Section>

        <Section>
          <SectionTitle>🔒 Keamanan & Privasi</SectionTitle>

          <FAQItem>
            <FAQQuestion>Data apa saja yang dikumpulkan?</FAQQuestion>
            <FAQAnswer>
              Kami hanya mengumpulkan data yang diperlukan untuk operasional akun: 
              email orang tua, progress permainan anak, dan data pembayaran 
              (diproses secara aman oleh payment gateway).
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>Bagaimana cara mengontrol waktu bermain anak?</FAQQuestion>
            <FAQAnswer>
              Di menu "Pengaturan Orang Tua", Anda bisa mengatur batas waktu 
              harian bermain dan jadwal istirahat.
            </FAQAnswer>
          </FAQItem>
        </Section>
      </Content>
    </PageWrapper>
  );
};

export default FAQPage;
